import { Close, FormatBold, FormatItalic, InsertLink, TextFields } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    MenuItem,
    MenuList,
    Popover,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Link } from '@tiptap/extension-link';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useState } from 'react';

import { PlaceholderExtension } from './PlaceholderExtension';
import { CATEGORY_LABELS, PLACEHOLDERS_BY_CATEGORY } from 'config/placeholders';

interface HeaderFooterEditorProps {
    content: string;
    onChange: (html: string) => void;
    onClose: () => void;
    type: 'header' | 'footer';
}

const formatButtonSx = (active: boolean) => ({
    width: 32,
    height: 32,
    minWidth: 32,
    p: 0,
    color: 'text.primary',
    bgcolor: active ? 'action.selected' : 'transparent',
    boxShadow: 'none',
});

export function HeaderFooterEditor({ content, onChange, onClose, type }: HeaderFooterEditorProps) {
    const [linkUrl, setLinkUrl] = useState('');
    const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
    const [placeholderAnchor, setPlaceholderAnchor] = useState<null | HTMLElement>(null);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: false,
                codeBlock: false,
                blockquote: false,
                horizontalRule: false,
                bulletList: false,
                orderedList: false,
                listItem: false,
            }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'text-primary underline',
                },
            }),
            PlaceholderExtension,
        ],
        content,
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            const textLength = editor.getText().length;

            if (textLength <= 200) {
                onChange(html);
            } else {
                editor.commands.undo();
            }
        },
        editorProps: {
            attributes: {
                class: 'prose prose-sm max-w-none focus:outline-none min-h-[80px] p-3 border rounded-md',
                style: 'color: #1f2937; background-color: #ffffff;',
            },
        },
    });

    useEffect(() => {
        return () => {
            editor?.destroy();
        };
    }, [editor]);

    const toggleBold = () => {
        editor?.chain().focus().toggleBold().run();
    };

    const toggleItalic = () => {
        editor?.chain().focus().toggleItalic().run();
    };

    const openLinkDialog = () => {
        const previousUrl = editor?.getAttributes('link').href;
        setLinkUrl(previousUrl || '');
        setIsLinkDialogOpen(true);
    };

    const setLink = () => {
        if (linkUrl === '') {
            editor?.chain().focus().extendMarkRange('link').unsetLink().run();
        } else {
            editor?.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
        }
        setIsLinkDialogOpen(false);
        setLinkUrl('');
    };

    const insertPlaceholder = (token: string) => {
        editor
            ?.chain()
            .focus()
            .command(({ tr }) => {
                const { from, to } = tr.selection;
                tr.insertText(token, from, to);
                return true;
            })
            .run();
        setPlaceholderAnchor(null);
        setSelectedCategory(null);
    };

    const handleCategorySelect = (category: string) => {
        setSelectedCategory(category);
    };

    const handleBackToCategories = () => {
        setSelectedCategory(null);
    };

    const getCharacterCount = () => {
        return editor?.getText().length || 0;
    };

    if (!editor) {
        return null;
    }

    return (
        <Stack spacing={2}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Edit {type === 'header' ? 'Header' : 'Footer'} Content
                </Typography>
                <IconButton size="small" onClick={onClose} aria-label="Close">
                    <Close fontSize="small" />
                </IconButton>
            </Stack>

            <Box
                sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                    bgcolor: 'background.paper',
                }}
            >
                <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ alignItems: 'center', borderBottom: 1, borderColor: 'divider', pb: 1 }}
                >
                    <IconButton
                        size="small"
                        onClick={toggleBold}
                        aria-label="Bold"
                        sx={formatButtonSx(editor.isActive('bold'))}
                    >
                        <FormatBold sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={toggleItalic}
                        aria-label="Italic"
                        sx={formatButtonSx(editor.isActive('italic'))}
                    >
                        <FormatItalic sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={openLinkDialog}
                        aria-label="Link"
                        sx={formatButtonSx(editor.isActive('link'))}
                    >
                        <InsertLink sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ alignSelf: 'center', height: 24, mx: 0.5 }}
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        onClick={(event) => setPlaceholderAnchor(event.currentTarget)}
                        startIcon={<TextFields sx={{ fontSize: 16 }} />}
                        sx={{
                            textTransform: 'none',
                            height: 32,
                            fontSize: 12,
                            color: 'text.primary',
                        }}
                    >
                        Insert Placeholder
                    </Button>
                </Stack>

                <EditorContent editor={editor} />

                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}
                >
                    {getCharacterCount()} / 200 characters
                </Typography>
            </Box>

            <Dialog
                open={isLinkDialogOpen}
                onClose={() => setIsLinkDialogOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Add Link</DialogTitle>
                <DialogContent>
                    <TextField
                        type="url"
                        fullWidth
                        size="small"
                        value={linkUrl}
                        onChange={(event) => setLinkUrl(event.target.value)}
                        placeholder="https://example.com"
                        autoFocus
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setIsLinkDialogOpen(false)}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={setLink} sx={{ textTransform: 'none' }}>
                        Add Link
                    </Button>
                </DialogActions>
            </Dialog>

            <Popover
                open={!!placeholderAnchor}
                anchorEl={placeholderAnchor}
                onClose={() => {
                    setPlaceholderAnchor(null);
                    setSelectedCategory(null);
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            minWidth: 250,
                            maxWidth: 300,
                            maxHeight: 400,
                        },
                    },
                }}
            >
                <MenuList dense>
                    {!selectedCategory ? (
                        Object.keys(PLACEHOLDERS_BY_CATEGORY).map((category) => (
                            <MenuItem
                                key={category}
                                onClick={() => handleCategorySelect(category)}
                                sx={{ justifyContent: 'space-between' }}
                            >
                                {CATEGORY_LABELS[category]}
                                <Box component="span" sx={{ ml: 1, color: 'text.disabled' }}>
                                    ›
                                </Box>
                            </MenuItem>
                        ))
                    ) : (
                        <>
                            <MenuItem
                                onClick={handleBackToCategories}
                                sx={{ fontWeight: 600, borderBottom: 1, borderColor: 'divider' }}
                            >
                                ‹ Back
                            </MenuItem>
                            {PLACEHOLDERS_BY_CATEGORY[selectedCategory]?.map((placeholder) => (
                                <MenuItem
                                    key={placeholder.token}
                                    onClick={() => insertPlaceholder(placeholder.token)}
                                >
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {placeholder.label}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {placeholder.token}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </>
                    )}
                </MenuList>
            </Popover>
        </Stack>
    );
}
